/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/role-supports-aria-props */
/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Product as ApiProduct } from '../services/api';
import { shopService, Shop, ShopProduct } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import CustomerHeader from '../components/CustomerHeader';
import ShopSelectionModal from '../components/ShopSelectionModal';
import CrossShopCart from '../components/CrossShopCart';
import { useTranslations } from '../hooks/useTranslations';
import { useCategoryTranslation } from '../utils/categoryTranslations';
import AddressModal from '../components/AddressModal';
import './CustomerHomePage.css';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  image: string;
  rating: number;
  brand: string;
  weight: string;
  category: string;
  inStock?: boolean;
}

interface CategoryCard {
  id: string;
  name: string;
  description: string;
  icon: string;
  gradient: string;
}

const CustomerHomePage: React.FC = () => {
  const navigate = useNavigate();
  const locationPath = useLocation();
  const { isAuthenticated, user } = useAuth();
  const t = useTranslations(); // Get translations
  const translateCategoryName = useCategoryTranslation(); // Get category translation function
  
  // State
  const [deliveryAddress, setDeliveryAddress] = useState(() => {
    const saved = localStorage.getItem('delivery_address');
    return saved || '123 Main Street, Downtown';
  });
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);
  const [nearbyShops, setNearbyShops] = useState<Shop[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingShops, setIsLoadingShops] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [apiCategories, setApiCategories] = useState<any[]>([]);
  
  // Fetch delivery address from API
  useEffect(() => {
    const loadDeliveryAddress = async () => {
      if (!isAuthenticated || !user) {
        // If not authenticated, use localStorage or default
        const saved = localStorage.getItem('delivery_address');
        if (saved) {
          setDeliveryAddress(saved);
        }
        return;
      }

      try {
        setLoadingAddress(true);
        const addresses = await apiService.getUserAddresses();
        
        if (addresses && addresses.length > 0) {
          // Find default address or use first address
          const defaultAddress = addresses.find((addr: any) => addr.is_default) || addresses[0];
          
          // Format address for display
          let formattedAddress = '';
          if (defaultAddress.street) {
            formattedAddress = defaultAddress.street;
            if (defaultAddress.city) {
              formattedAddress += `, ${defaultAddress.city}`;
            }
            if (defaultAddress.state) {
              formattedAddress += `, ${defaultAddress.state}`;
            }
            if (defaultAddress.pincode) {
              formattedAddress += ` ${defaultAddress.pincode}`;
            }
          } else if (defaultAddress.address_line1) {
            // Alternative address format
            formattedAddress = defaultAddress.address_line1;
            if (defaultAddress.address_line2) {
              formattedAddress += `, ${defaultAddress.address_line2}`;
            }
            if (defaultAddress.city) {
              formattedAddress += `, ${defaultAddress.city}`;
            }
            if (defaultAddress.state) {
              formattedAddress += `, ${defaultAddress.state}`;
            }
            if (defaultAddress.postal_code) {
              formattedAddress += ` ${defaultAddress.postal_code}`;
            }
          } else if (defaultAddress.full_address) {
            formattedAddress = defaultAddress.full_address;
          } else {
            // Fallback: use any available address field
            formattedAddress = defaultAddress.address || defaultAddress.location || 'Address not specified';
          }
          
          if (formattedAddress) {
            setDeliveryAddress(formattedAddress);
            localStorage.setItem('delivery_address', formattedAddress);
          }
        }
      } catch (error) {
        console.error('Error loading delivery address from API:', error);
        // Fallback to localStorage if API fails
        const saved = localStorage.getItem('delivery_address');
        if (saved) {
          setDeliveryAddress(saved);
        }
      } finally {
        setLoadingAddress(false);
      }
    };

    loadDeliveryAddress();
  }, [isAuthenticated, user]);
  
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [selectedDealType, setSelectedDealType] = useState<string>('top-deals');
  
  // Shop Selection Modal State
  const [showShopModal, setShowShopModal] = useState(false);
  const [selectedProductForCart, setSelectedProductForCart] = useState<Product | null>(null);
  
  // Cart Modal State
  const [showCartModal, setShowCartModal] = useState(false);
  const [cartItems, setCartItems] = useState<Array<{ product: ShopProduct; shop: Shop; quantity: number }>>([]);
  
  // Wishlist state
  const [wishlist, setWishlist] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  // Wishlist functions
  const isInWishlist = (productId: string): boolean => {
    return wishlist.some(item => String(item.id) === String(productId));
  };

  const handleAddToWishlist = (product: any, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (isInWishlist(String(product.id))) {
      // Remove from wishlist
      const updated = wishlist.filter(item => String(item.id) !== String(product.id));
      setWishlist(updated);
      localStorage.setItem('wishlist', JSON.stringify(updated));
      // Dispatch event to update wishlist count in header
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    } else {
      // Add to wishlist
      const wishlistItem = {
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        discount: product.discount || 0,
        image: product.image || '📦',
        rating: product.rating || 0,
        brand: product.brand || '',
        weight: product.weight || '',
        category: product.category || ''
      };
      const updated = [...wishlist, wishlistItem];
      setWishlist(updated);
      localStorage.setItem('wishlist', JSON.stringify(updated));
      // Dispatch event to update wishlist count in header
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    }
  };
  
  // Promotional Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Promotional slides data
  // Promotional slides data - PRIMARY CTA: Browse Categories
  const promotionalSlides = [
    {
      id: 1,
      title: 'Browse All Products',
      description: 'Discover thousands of products from local shops. Shop by category and find exactly what you need.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop',
      ctaText: 'Browse Categories',
      ctaLink: '/products',
      isPrimary: true
    },
    {
      id: 2,
      title: 'Fresh Groceries & Daily Essentials',
      description: 'Get the freshest produce, dairy, and bakery items delivered right to your door.',
      image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop',
      ctaText: 'Shop Fresh Products',
      ctaLink: '#section-fresh',
      isPrimary: false
    },
    {
      id: 3,
      title: 'Explore Local Shops',
      description: 'Discover amazing local shops and get fresh products delivered to your doorstep.',
      image: 'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=800&h=600&fit=crop',
      ctaText: 'Explore Shops',
      ctaLink: '/explore-shops',
      isPrimary: false
    },
    {
      id: 4,
      title: 'Exclusive Weekly Deals',
      description: 'Save up to 50% on selected items from your favorite local shops.',
      image: 'https://images.unsplash.com/photo-1556740758-90de374c12ad?w=800&h=600&fit=crop',
      ctaText: 'View All Deals',
      ctaLink: '/deals',
      isPrimary: false
    }
  ];

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % promotionalSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, promotionalSlides.length]);

  // Handle navigation
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false); // Pause auto-play when manually navigating
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % promotionalSlides.length);
    setIsAutoPlaying(false);
  };

  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + promotionalSlides.length) % promotionalSlides.length);
    setIsAutoPlaying(false);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  // Static sample data for Weekly Specials
  const weeklySpecials = [
    {
      id: 1,
      title: 'Fresh Produce Week',
      description: 'Save up to 50% on fresh fruits and vegetables',
      image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop&q=80',
      discount: '50%',
      category: 'Fruits & Vegetables'
    },
    {
      id: 2,
      title: 'Dairy Delights',
      description: 'Get 40% off on all dairy products this week',
      image: 'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=800&h=600&fit=crop&q=80',
      discount: '40%',
      category: 'Dairy'
    },
    {
      id: 3,
      title: 'Bakery Bonanza',
      description: 'Fresh breads and pastries at 35% discount',
      image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=800&h=600&fit=crop&q=80',
      discount: '35%',
      category: 'Bakery'
    },
    {
      id: 4,
      title: 'Meat & Seafood',
      description: 'Premium quality meats with 45% savings',
      image: 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=800&h=600&fit=crop&q=80',
      discount: '45%',
      category: 'Meat & Seafood'
    }
  ];

  // Static sample data for Recipe Ideas
  const recipeIdeas = [
    {
      id: 1,
      title: 'Quick Pasta Primavera',
      description: 'Fresh vegetables in a creamy sauce - ready in 25 minutes',
      image: 'https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=800&h=600&fit=crop&q=80',
      time: '25 min',
      servings: '4 servings',
      difficulty: 'Easy'
    },
    {
      id: 2,
      title: 'Grilled Chicken Salad',
      description: 'Healthy and delicious grilled chicken with mixed greens',
      image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&h=600&fit=crop&q=80',
      time: '20 min',
      servings: '2 servings',
      difficulty: 'Easy'
    },
    {
      id: 3,
      title: 'Vegetable Stir Fry',
      description: 'Colorful vegetables in a savory sauce - perfect for dinner',
      image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&h=600&fit=crop&q=80',
      time: '15 min',
      servings: '3 servings',
      difficulty: 'Easy'
    }
  ];
  
  // Listen for language changes from other components
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      setLanguage(event.detail.language);
    };
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
  }, []);

  // Check if first time user and get cart count
  useEffect(() => {
    // Check if this is first time user
    const hasVisitedBefore = localStorage.getItem('has_visited_before');
    if (!hasVisitedBefore && isAuthenticated) {
      setIsFirstTimeUser(true);
      localStorage.setItem('has_visited_before', 'true');
    }

    // Get cart count from localStorage
    const updateCartCount = () => {
      try {
        const cartData = localStorage.getItem('cart');
        const multiShopCart = localStorage.getItem('multiShopCart');
        let count = 0;
        
        if (cartData) {
          const cart = JSON.parse(cartData);
          count += Array.isArray(cart) ? cart.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
        }
        
        if (multiShopCart) {
          const multiCart = JSON.parse(multiShopCart);
          // Handle both cart structures
          if (Array.isArray(multiCart) && multiCart.length > 0) {
            // Check if it's the nested format: { shop, items: [{ product, quantity }] }
            if (multiCart[0]?.shop && multiCart[0]?.items) {
              // Nested structure: array of shop carts
              multiCart.forEach((shopCart: any) => {
                if (shopCart.items && Array.isArray(shopCart.items)) {
                  count += shopCart.items.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
                }
              });
            } else if (multiCart[0]?.product || multiCart[0]?.quantity !== undefined) {
              // Flat structure: array of { product, shop, quantity }
              count += multiCart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
            }
          }
        }
        
        setCartCount(count);
      } catch (error) {
        console.error('Error reading cart:', error);
      }
    };

    updateCartCount();
    
    // Listen for cart update events
    const handleCartUpdate = () => {
      updateCartCount();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    // Update cart count periodically
    const interval = setInterval(updateCartCount, 1000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [isAuthenticated]);

  // Category Cards (matching mobile app)
  const categoryCards: CategoryCard[] = [
    {
      id: 'fruits-veggies',
      name: 'Fresh Fruits & Veggies',
      description: 'Up to 40% off on organic produce',
      icon: '🥬',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    {
      id: 'dairy',
      name: 'Dairy Delights',
      description: 'Farm fresh delivered daily',
      icon: '🥛',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    },
    {
      id: 'bakery',
      name: 'Bakery Fresh',
      description: 'Artisan breads & pastries',
      icon: '🥖',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    }
  ];

  // Helper function to get category image path from local assets (matching mobile app)
  const getCategoryImagePath = (categoryName: string): string | null => {
    if (!categoryName) return null;
    
    const name = categoryName.toLowerCase().trim();
    const imageMap: { [key: string]: string } = {
      // Fruits & Vegetables
      'fruits': '/fruits & vegetables.jpg',
      'vegetables': '/fruits & vegetables.jpg',
      'fruits & vegetables': '/fruits & vegetables.jpg',
      'fruits-vegetables': '/fruits & vegetables.jpg',
      'fruits and vegetables': '/fruits & vegetables.jpg',
      'fruits & veg': '/fruits & vegetables.jpg',
      'fruits-veg': '/fruits & vegetables.jpg',
      'produce': '/fruits & vegetables.jpg',
      
      // Home Food / Cooking
      'home food': '/home cooking image.jpg',
      'home-food': '/home cooking image.jpg',
      'home cooking': '/home cooking image.jpg',
      'homefood': '/home cooking image.jpg',
      'cooking': '/home cooking image.jpg',
      
      // Pickles
      'pickles': '/pickle-shop-image.png',
      'pickle': '/pickle-shop-image.png',
      
      // Pharmacy / Health
      'pharmacy': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'pharmacies': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'medication': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'medications': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'medicine': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'ayurvedic': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'ayurveda': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'dental': 'http://localhost:3000/api/v1/uploads/category-images/Dental-Care.jpg',
      'dental care': 'http://localhost:3000/api/v1/uploads/category-images/Dental-Care.jpg',
      'dental-care': 'http://localhost:3000/api/v1/uploads/category-images/Dental-Care.jpg',
      'wellness': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'health': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
      'medical': 'http://localhost:3000/api/v1/uploads/category-images/Pharmacy.jpg',
    };
    
    // Check for exact matches
    if (imageMap[name]) {
      const imageUrl = imageMap[name];
      // Add cache-busting parameter for API URLs to force reload
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return `${imageUrl}?v=${Date.now()}`;
      }
      return imageUrl;
    }
    
    // Check for partial matches
    for (const [key, imagePath] of Object.entries(imageMap)) {
      if (name.includes(key) || key.includes(name)) {
        // Add cache-busting parameter for API URLs to force reload
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
          return `${imagePath}?v=${Date.now()}`;
        }
        return imagePath;
      }
    }
    
    return null;
  };

  // Helper function to get category icon based on name
  const getCategoryIcon = (categoryName: string): string => {
    const iconMap: { [key: string]: string } = {
      'bakeries': '🥖', 'bakery': '🥖', 'bread': '🥖', 'pastries': '🥖',
      'sweet': '🍬', 'sweets': '🍬', 'candy': '🍬',
      'home food': '🏠', 'home-food': '🏠', 'homefood': '🏠',
      'pickles': '🥒', 'pickle': '🥒',
      'dairy': '🥛', 'milk': '🥛', 'cheese': '🥛',
      'fruits': '🍎', 'vegetables': '🍎', 'fruits & veg': '🍎', 'fruits-veg': '🍎', 'produce': '🍎',
      'flowers': '🌸', 'flower': '🌸',
      'meat': '🥩', 'chicken': '🥩', 'fish': '🥩',
      'kirana': '🏪', 'grocery': '🏪', 'groceries': '🏪',
      'organic': '🌿', 'organic products': '🌿',
      'rice': '🌾', 'grains': '🌾', 'rice & grains': '🌾', 'rice-grains': '🌾',
      'flour': '🌾', 'flour-mills': '🌾', 'mills': '🌾',
      'dry fruits': '🥜', 'dry-fruits': '🥜', 'nuts': '🥜',
      'electrical': '⚡', 'electricals': '⚡', 'electronics': '⚡', 'electronic': '⚡', 'appliance': '⚡', 'appliances': '⚡',
      'pharmacy': '💊', 'medications': '💊', 'medicine': '💊',
      'ayurvedic': '🌿', 'ayurveda': '🌿',
      'dental': '🦷', 'dental care': '🦷', 'dental-care': '🦷',
      'wellness': '🧘', 'health': '🧘'
    };
    
    const lowerName = categoryName.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerName.includes(key)) {
        return icon;
      }
    }
    return '📦'; // Default icon
  };

  // Map API categories to Food, Fresh, Mart sections
  const mapCategoriesToSections = (categories: any[]) => {
    const foodKeywords = ['bakery', 'bakeries', 'sweet', 'home food', 'pickle', 'restaurant', 'food'];
    const freshKeywords = ['dairy', 'fruits', 'vegetables', 'flowers', 'meat', 'chicken', 'fish', 'produce', 'fresh'];
    const martKeywords = ['kirana', 'grocery', 'organic', 'rice', 'rice-traders', 'traders', 'grains', 'flour', 'flour-mills', 'mills', 'dry fruits', 'dry fruit', 'dry-fruits', 'dry-fruit', 'nuts', 'mart', 'store', 'stores', 'electrical', 'electricals', 'electronics', 'electronic', 'appliance', 'appliances', 'home appliance', 'home appliances'];
    const healthKeywords = ['pharmacy', 'pharmacies', 'pharmacist', 'medication', 'medications', 'medicine', 'medicines', 'ayurvedic', 'ayurveda', 'dental', 'dentist', 'wellness', 'health', 'medical', 'healthcare'];

    // Track used category IDs to ensure each category appears in only one section
    const usedCategoryIds = new Set<string>();

    // Helper function to check if category matches keywords and hasn't been used
    const matchesKeywords = (cat: any, keywords: string[]): boolean => {
      const name = (cat.name || '').toLowerCase();
      const topCategory = (cat.top_category || '').toLowerCase();
      const categoryId = cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || '';
      
      // Skip if already used
      if (usedCategoryIds.has(categoryId)) {
        return false;
      }
      
      return keywords.some(keyword => name.includes(keyword) || topCategory.includes(keyword));
    };

    // Process in priority order: Food > Fresh > Mart > Health
    const foodCategories = categories.filter(cat => {
      if (matchesKeywords(cat, foodKeywords)) {
        const categoryId = cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || '';
        usedCategoryIds.add(categoryId);
        return true;
      }
      return false;
    });

    const freshCategories = categories.filter(cat => {
      if (matchesKeywords(cat, freshKeywords)) {
        const categoryId = cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || '';
        usedCategoryIds.add(categoryId);
        return true;
      }
      return false;
    });

    const martCategories = categories.filter(cat => {
      const name = (cat.name || '').toLowerCase();
      // Exclude "Category Shops" from Mart section
      if (name.includes('category shops') || name === 'category shops') {
        return false;
      }
      if (matchesKeywords(cat, martKeywords)) {
        const categoryId = cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || '';
        usedCategoryIds.add(categoryId);
        return true;
      }
      return false;
    });

    const healthCategories = categories.filter(cat => {
      if (matchesKeywords(cat, healthKeywords)) {
        const categoryId = cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || '';
        usedCategoryIds.add(categoryId);
        return true;
      }
      return false;
    });

    return {
      food: foodCategories,
      fresh: freshCategories,
      mart: martCategories,
      health: healthCategories
    };
  };

  // Category Sections - dynamically populated from API
  const getCategorySections = () => {
    if (apiCategories.length > 0) {
      const mapped = mapCategoriesToSections(apiCategories);
      const sections = [
        {
          title: t.food || 'Food',
          categories: mapped.food
            .map(cat => ({
              id: cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
              name: translateCategoryName(cat.display_name || cat.name || 'Unknown'),
              icon: cat.icon || getCategoryIcon(cat.name || ''),
              image: cat.image_url ? getValidImageUrl(cat.image_url) : (cat.image ? getValidImageUrl(cat.image) : (getCategoryImagePath(cat.name || '') || null)),
              categoryId: cat.id,
              categoryName: cat.name
            }))
            // Remove duplicates by name and ID
            .filter((cat, index, self) => 
              index === self.findIndex((c) => 
                c.name.toLowerCase() === cat.name.toLowerCase() || 
                c.id === cat.id
              )
            )
            .slice(0, 8)
        },
        {
          title: t.fresh || 'Fresh',
          categories: mapped.fresh
            .map(cat => ({
              id: cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
              name: translateCategoryName(cat.display_name || cat.name || 'Unknown'),
              icon: cat.icon || getCategoryIcon(cat.name || ''),
              image: cat.image_url ? getValidImageUrl(cat.image_url) : (cat.image ? getValidImageUrl(cat.image) : (getCategoryImagePath(cat.name || '') || null)),
              categoryId: cat.id,
              categoryName: cat.name
            }))
            // Remove duplicates by name and ID
            .filter((cat, index, self) => 
              index === self.findIndex((c) => 
                c.name.toLowerCase() === cat.name.toLowerCase() || 
                c.id === cat.id
              )
            )
            .slice(0, 8)
        },
        {
          title: t.mart || 'Mart',
          categories: mapped.mart
            .map(cat => ({
              id: cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
              name: translateCategoryName(cat.display_name || cat.name || 'Unknown'),
              icon: cat.icon || getCategoryIcon(cat.name || ''),
              image: cat.image_url ? getValidImageUrl(cat.image_url) : (cat.image ? getValidImageUrl(cat.image) : (getCategoryImagePath(cat.name || '') || null)),
              categoryId: cat.id,
              categoryName: cat.name
            }))
            .filter(cat => cat.image !== null && cat.image !== undefined && cat.image !== '')
            // Remove duplicates by name and ID
            .filter((cat, index, self) => 
              index === self.findIndex((c) => 
                c.name.toLowerCase() === cat.name.toLowerCase() || 
                c.id === cat.id
              )
            )
            .slice(0, 8)
        }
      ];

      // Add Health section
      sections.push({
        title: t.health || 'Health-Wellness',
        categories: mapped.health
          .map(cat => {
            const categoryImagePath = getCategoryImagePath(cat.name || '');
            const imageUrl = cat.image_url 
              ? getValidImageUrl(cat.image_url) 
              : (cat.image 
                ? getValidImageUrl(cat.image) 
                : (categoryImagePath ? getValidImageUrl(categoryImagePath) : null));
            
            return {
              id: cat.id || cat.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
              name: translateCategoryName(cat.display_name || cat.name || 'Unknown'),
              icon: cat.icon || getCategoryIcon(cat.name || ''),
              image: imageUrl,
              categoryId: cat.id,
              categoryName: cat.name
            };
          })
          .filter(cat => {
            // Filter out categories without valid images
            if (!cat.image || cat.image === null || cat.image === undefined || cat.image === '') {
              return false;
            }
            // Check if it's the fallback image (data URI) - we don't want those
            if (cat.image.startsWith('data:image')) {
              return false;
            }
            return true;
          })
          // Remove duplicates by name and ID (keep first occurrence)
          .filter((cat, index, self) => 
            index === self.findIndex((c) => 
              c.name.toLowerCase() === cat.name.toLowerCase() || 
              c.id === cat.id
            )
          )
          .slice(0, 8)
      });

      return sections;
    }

    // Fallback to static categories if API fails
    return [
      {
        title: t.food || 'Food',
        categories: [
          { id: 'bakeries', name: translateCategoryName('Bakeries'), icon: '🥖', image: null, categoryId: 'bakeries', categoryName: 'bakeries' },
          { id: 'sweet-shops', name: translateCategoryName('Sweet Shops'), icon: '🍬', image: null, categoryId: 'sweet-shops', categoryName: 'sweet-shops' },
          { id: 'home-food', name: translateCategoryName('Home Food'), icon: '🏠', image: null, categoryId: 'home-food', categoryName: 'home-food' },
          { id: 'pickles', name: translateCategoryName('Pickles'), icon: '🥒', image: null, categoryId: 'pickles', categoryName: 'pickles' }
        ]
      },
      {
        title: t.fresh || 'Fresh',
        categories: [
          { id: 'dairy', name: translateCategoryName('Dairy'), icon: '🥛', image: null, categoryId: 'dairy', categoryName: 'dairy' },
          { id: 'fruits-veg', name: translateCategoryName('Fruits & Veg'), icon: '🍎', image: null, categoryId: 'fruits-veg', categoryName: 'fruits-veg' },
          { id: 'flowers', name: translateCategoryName('Flowers'), icon: '🌸', image: null, categoryId: 'flowers', categoryName: 'flowers' },
          { id: 'meat', name: translateCategoryName('Meat'), icon: '🥩', image: null, categoryId: 'meat', categoryName: 'meat' }
        ]
      },
      {
        title: t.mart || 'Mart',
        categories: [
          { id: 'kirana', name: translateCategoryName('Kirana'), icon: '🏪', image: null, categoryId: 'kirana', categoryName: 'kirana' },
          { id: 'organic', name: translateCategoryName('Organic'), icon: '🌿', image: null, categoryId: 'organic', categoryName: 'organic' },
          { id: 'rice-grains', name: translateCategoryName('Rice & Grains'), icon: '🌾', image: null, categoryId: 'rice-grains', categoryName: 'rice-grains' },
          { id: 'dry-fruits', name: translateCategoryName('Dry Fruits'), icon: '🥜', image: null, categoryId: 'dry-fruits', categoryName: 'dry-fruits' }
        ]
      },
      {
        title: t.health || 'Health-Wellness',
        categories: [
          { id: 'pharmacy', name: translateCategoryName('Pharmacy'), icon: '💊', image: null, categoryId: 'pharmacy', categoryName: 'pharmacy' },
          { id: 'ayurvedic', name: translateCategoryName('Ayurvedic'), icon: '🌿', image: null, categoryId: 'ayurvedic', categoryName: 'ayurvedic' },
          { id: 'dental-care', name: translateCategoryName('Dental Care'), icon: '🦷', image: null, categoryId: 'dental-care', categoryName: 'dental-care' },
          { id: 'wellness', name: translateCategoryName('Wellness'), icon: '🧘', image: null, categoryId: 'wellness', categoryName: 'wellness' }
        ]
      }
    ];
  };

  const categorySections = getCategorySections();

  // Flattened category list for search
  const allCategories = categorySections.flatMap(section => section.categories);

  // Get all category IDs and names from category sections to filter duplicates from categoryCards
  const categorySectionIds = new Set(
    categorySections.flatMap(section => 
      section.categories.map((cat: any) => cat.id?.toLowerCase() || cat.name?.toLowerCase() || '')
    )
  );
  const categorySectionNames = new Set(
    categorySections.flatMap(section => 
      section.categories.map((cat: any) => cat.name?.toLowerCase() || '')
    )
  );

  // Filter categoryCards to exclude duplicates that appear in categorySections
  const filteredCategoryCards = categoryCards.filter(card => {
    const cardId = card.id.toLowerCase();
    const cardName = card.name.toLowerCase();
    
    // Check if this card's ID or name matches any category in sections
    const isDuplicate = categorySectionIds.has(cardId) || 
                       categorySectionNames.has(cardName) ||
                       // Also check for partial matches (e.g., "Dairy Delights" vs "Dairy")
                       Array.from(categorySectionNames).some(sectionName => 
                         cardName.includes(sectionName) || sectionName.includes(cardName)
                       );
    
    return !isDuplicate;
  });

  useEffect(() => {
    loadCategories();
    loadPopularProducts();
    loadNearbyShops();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      // Fetch only level 3 product categories (lvlcategory=3) - NOT shop types
      const categories = await apiService.getCategoriesForSearch(3);
      console.log('📦 Loaded level 3 product categories from API:', categories.length);
      
      // Filter out shop types explicitly
      const shopTypeKeywords = ['shop', 'shops', 'kirana', 'bakery', 'bakeries', 'pharmacy', 'pharmacies', 'restaurant', 'restaurants', 'store', 'stores', 'mart', 'marts'];
      const productCategories = categories.filter(cat => {
        const name = (cat.name || '').toLowerCase();
        const displayName = (cat.display_name || '').toLowerCase();
        // Exclude categories that are clearly shop types
        return !shopTypeKeywords.some(keyword => 
          name.includes(keyword) || displayName.includes(keyword)
        );
      });
      
      console.log(`✅ Filtered to ${productCategories.length} product categories (removed ${categories.length - productCategories.length} shop types)`);
      console.log('📦 Product categories sample:', productCategories.slice(0, 3));
      setApiCategories(productCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      // Keep empty array, will use fallback categories
      setApiCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadPopularProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const products = await apiService.getProducts();
      // Filter products with rating >= 4.5, then map to match Product interface
      const highRatedProducts = products.filter((p: any) => {
        const rating = p.rating || 0;
        return rating >= 4.5;
      });
      
      const mappedProducts: Product[] = highRatedProducts.map((p: any) => ({
        id: String(p.id),
        name: p.name || 'Product',
        price: p.price || 0,
        originalPrice: p.originalPrice || p.original_price,
        discount: p.discount || (p.originalPrice && p.price ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100) : undefined),
        image: p.image || '📦',
        rating: p.rating || 4.5,
        brand: p.brand || 'Brand',
        weight: p.weight || '1 kg',
        category: p.category || 'general'
      }));
      
      console.log(`✅ Loaded ${mappedProducts.length} popular products with rating >= 4.5 (out of ${products.length} total products)`);
      setPopularProducts(mappedProducts);
    } catch (error) {
      console.error('Error loading products:', error);
      // Set mock products as fallback (only rating >= 4.5)
      setPopularProducts([
        {
          id: '1',
          name: 'Fresh Red Apples',
          price: 149,
          originalPrice: 199,
          discount: 25,
          image: 'https://images.unsplash.com/photo-1560806887-1e4cd0b6cbd6?w=600&h=600&fit=crop&q=80',
          rating: 4.5,
          brand: "Nature's Best",
          weight: '1 kg',
          category: 'fruits'
        },
        {
          id: '2',
          name: 'Organic Whole Milk',
          price: 68,
          originalPrice: 75,
          discount: 9,
          image: 'https://images.unsplash.com/photo-1563636619-e9143da7973b?w=600&h=600&fit=crop&q=80',
          rating: 4.7,
          brand: 'Farm Fresh',
          weight: '1 L',
          category: 'dairy'
        },
        {
          id: '4',
          name: 'Basmati Rice',
          price: 320,
          originalPrice: 380,
          discount: 16,
          image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600&h=600&fit=crop&q=80',
          rating: 4.8,
          brand: 'Golden Harvest',
          weight: '5 kg',
          category: 'grains'
        }
      ]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadNearbyShops = async () => {
    try {
      setIsLoadingShops(true);
      const shops = await shopService.getShops();
      console.log('CustomerHomePage: Raw shops from API:', shops);
      // Normalize shop data to ensure logo field is properly mapped from various image field names
      const normalizedShops = shops.map((shop: any) => {
        // Check all possible image field names (in order of preference)
        const rawLogo = shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo || shop.banner_url || undefined;
        // Use getValidImageUrl to ensure protocol is present and URL is valid
        const logo = rawLogo ? getValidImageUrl(rawLogo) : undefined;
        console.log(`CustomerHomePage: Shop "${shop.name}" - image fields:`, {
          logo: shop.logo,
          logo_url: shop.logo_url,
          image: shop.image,
          image_url: shop.image_url,
          photo: shop.photo,
          banner_url: shop.banner_url,
          rawLogo: rawLogo,
          processedLogo: logo
        });
        return {
          ...shop,
          logo: logo,
        };
      });
      console.log('CustomerHomePage: Normalized shops:', normalizedShops);
      // Show all shops - grid will dynamically adjust
      setNearbyShops(normalizedShops);
    } catch (error) {
      console.error('Error loading shops:', error);
      // Set mock shops as fallback
      setNearbyShops([
        {
          id: 'shop-1',
          name: 'Fresh Mart Superstore',
          address: '123 Main St',
          city: 'Downtown',
          state: 'NY',
          country: 'USA',
          type: 'grocery',
          description: 'Fresh Produce',
          is_active: true
        },
        {
          id: 'shop-2',
          name: 'Organic Haven',
          address: '456 Green Ave',
          city: 'Uptown',
          state: 'NY',
          country: 'USA',
          type: 'specialty',
          description: 'Organic Healthy Natural',
          is_active: true
        }
      ]);
    } finally {
      setIsLoadingShops(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    // Search can be by shop, category, or product/brand
    // Check if query matches a category name
    const matchingCategory = allCategories.find(
      (cat: { id: string; name: string; icon: string }) => 
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        searchQuery.toLowerCase().includes(cat.name.toLowerCase())
    );
    
    if (matchingCategory) {
      // Navigate to browse-all with category filter
      const category = allCategories.find((cat: any) => cat.id === matchingCategory.id);
      if (category && (category as any).categoryId) {
        navigate('/browse-all', {
          state: {
            categoryId: (category as any).categoryId,
            categoryName: (category as any).categoryName || (category as any).name,
            filterType: 'category',
            searchQuery: searchQuery
          }
        });
      } else {
        navigate('/browse-all', {
          state: {
            categoryName: matchingCategory.id,
            filterType: 'category',
            searchQuery: searchQuery
          }
        });
      }
    } else {
      // Search all (products, shops, brands) using API
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleProductAdd = (product: Product) => {
    // Show shop selection modal
    setSelectedProductForCart(product);
    setShowShopModal(true);
  };

  const handleShopSelect = (shop: Shop, product: Product) => {
    // Add product to cart with selected shop
    try {
      // Get existing cart from localStorage
      const existingCart = localStorage.getItem('multiShopCart');
      let cart: Array<{ shop: Shop; items: Array<{ product: ShopProduct; quantity: number }> }> = [];
      
      if (existingCart) {
        try {
          cart = JSON.parse(existingCart);
        } catch (e) {
          console.error('Error parsing cart:', e);
        }
      }

      // Convert product to ShopProduct format
      const shopProduct: ShopProduct = {
        ...product as any,
        id: String(product.id),
        shopId: shop.id,
        shop_id: shop.id,
        category_id: (product as any).category_id || '',
        stock: (product as any).stock_quantity || 0,
        inStock: (product as any).inStock !== undefined ? (product as any).inStock : true,
        createdAt: (product as any).created_at || new Date().toISOString(),
        updatedAt: (product as any).updated_at || new Date().toISOString()
      };

      // Find or create shop entry in cart
      let shopCart = cart.find(c => c.shop.id === shop.id);
      
      if (!shopCart) {
        shopCart = { shop, items: [] };
        cart.push(shopCart);
      }

      // Check if product already exists in this shop's cart
      const existingItem = shopCart.items.find(item => item.product.id === String(product.id));
      
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        shopCart.items.push({ product: shopProduct, quantity: 1 });
      }

      // Save to localStorage
      try {
        localStorage.setItem('multiShopCart', JSON.stringify(cart));
        console.log('🛒 CustomerHomePage: Cart saved to localStorage');
        
        // Update cart count
        const totalCount = cart.reduce((sum, shopCart) => 
          sum + shopCart.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
        );
        setCartCount(totalCount);

        // Trigger cart update event immediately, then again with delay as fallback
        const event = new CustomEvent('cartUpdated', { detail: { cartCount: totalCount } });
        window.dispatchEvent(event);
        console.log('🛒 CustomerHomePage: cartUpdated event dispatched immediately, count:', totalCount);
        // Also dispatch with small delay as fallback
        setTimeout(() => {
          window.dispatchEvent(event);
          console.log('🛒 CustomerHomePage: cartUpdated event dispatched (fallback)');
        }, 10);
      } catch (error) {
        console.error('Error saving cart:', error);
        throw error;
      }

      console.log('✅ Product added to cart:', { product: product.name, shop: shop.name });
      
      // Close modal
      setShowShopModal(false);
      setSelectedProductForCart(null);
      
      // Show success message
      alert(`Added ${product.name} to cart from ${shop.name}`);
    } catch (error) {
      console.error('Error adding product to cart:', error);
      alert('Failed to add product to cart. Please try again.');
    }
  };

  // Load cart items from localStorage
  const loadCartItems = () => {
    try {
      const multiShopCartStr = localStorage.getItem('multiShopCart');
      const items: Array<{ product: ShopProduct; shop: Shop; quantity: number }> = [];
      
      if (multiShopCartStr) {
        const cart = JSON.parse(multiShopCartStr);
        if (Array.isArray(cart) && cart.length > 0) {
          cart.forEach((cartEntry: any) => {
            if (cartEntry.items && Array.isArray(cartEntry.items)) {
              // Structure: { shop, items: [{ product, quantity }] }
              cartEntry.items.forEach((item: any) => {
                items.push({
                  product: item.product,
                  shop: cartEntry.shop,
                  quantity: item.quantity || 1
                });
              });
            } else if (cartEntry.product && cartEntry.shop) {
              // Structure: { product, shop, quantity }
              items.push({
                product: cartEntry.product,
                shop: cartEntry.shop,
                quantity: cartEntry.quantity || 1
              });
            }
          });
        }
      }
      
      setCartItems(items);
    } catch (error) {
      console.error('Error loading cart items:', error);
      setCartItems([]);
    }
  };

  // Handle cart item updates
  const handleUpdateQuantity = (productId: string, shopId: string, quantity: number) => {
    try {
      const multiShopCartStr = localStorage.getItem('multiShopCart');
      if (!multiShopCartStr) return;
      
      const cart = JSON.parse(multiShopCartStr);
      
      cart.forEach((cartEntry: any) => {
        if (cartEntry.shop.id === shopId) {
          if (cartEntry.items && Array.isArray(cartEntry.items)) {
            const item = cartEntry.items.find((i: any) => i.product.id === productId);
            if (item) {
              if (quantity <= 0) {
                cartEntry.items = cartEntry.items.filter((i: any) => i.product.id !== productId);
              } else {
                item.quantity = quantity;
              }
            }
          }
        }
      });
      
      // Remove empty shop entries
      const filteredCart = cart.filter((cartEntry: any) => 
        !cartEntry.items || (Array.isArray(cartEntry.items) && cartEntry.items.length > 0)
      );
      
      localStorage.setItem('multiShopCart', JSON.stringify(filteredCart));
      loadCartItems();
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  };

  const handleRemoveItem = (productId: string, shopId: string) => {
    handleUpdateQuantity(productId, shopId, 0);
  };

  // Handle checkout from cart modal
  const handleCartCheckout = (shopOrders: { shop: Shop; items: Array<{ product: ShopProduct; shop: Shop; quantity: number }> }[]) => {
    // Convert to checkout format
    const cartItemsForCheckout: any[] = [];
    let totalAmount = 0;
    
    shopOrders.forEach(shopOrder => {
      shopOrder.items.forEach(item => {
        cartItemsForCheckout.push({
          id: String(item.product.id),
          name: item.product.name,
          price: item.product.price || 0,
          quantity: item.quantity,
          image: item.product.image || '',
          brand: item.product.brand || ''
        });
        totalAmount += (item.product.price || 0) * item.quantity;
      });
    });
    
    // Close cart modal
    setShowCartModal(false);
    
    // Navigate to checkout page
    navigate('/checkout', {
      state: {
        cartItems: cartItemsForCheckout,
        totalAmount
      }
    });
  };

  // Map category names to shop types for shop filtering
  const getShopTypeFromCategory = (categoryName: string): string | null => {
    const lowerName = categoryName.toLowerCase();
    const categoryToShopType: { [key: string]: string } = {
      // Bakery categories
      'bakeries': 'bakery',
      'bakery': 'bakery',
      'bread': 'bakery',
      'pastries': 'bakery',
      
      // Sweet Shops
      'sweet shops': 'specialty',
      'sweet-shops': 'specialty',
      'sweet': 'specialty',
      'sweets': 'specialty',
      'candy': 'specialty',
      
      // Home Food / Restaurant
      'home food': 'restaurant',
      'home-food': 'restaurant',
      'homefood': 'restaurant',
      'restaurant': 'restaurant',
      'restaurants': 'restaurant',
      
      // Pickles
      'pickles': 'specialty',
      'pickle': 'specialty',
      'pickle shops': 'specialty',
      
      // Dairy
      'dairy': 'grocery',
      'milk': 'grocery',
      'cheese': 'grocery',
      
      // Fresh categories
      'fruits': 'grocery',
      'vegetables': 'grocery',
      'fruits & veg': 'grocery',
      'fruits-veg': 'grocery',
      'fruits vegetables': 'grocery',
      'produce': 'grocery',
      'flowers': 'specialty',
      'flower': 'specialty',
      'meat': 'grocery',
      'chicken': 'grocery',
      'fish': 'grocery',
      
      // Mart / Grocery categories
      'grocery': 'grocery',
      'groceries': 'grocery',
      'kirana': 'grocery',
      'organic': 'specialty',
      'organic products': 'specialty',
      'rice': 'grocery',
      'grains': 'grocery',
      'rice & grains': 'grocery',
      'rice-grains': 'grocery',
      'dry fruits': 'specialty',
      'dry-fruits': 'specialty',
      'nuts': 'specialty',
      
      // Health / Medical categories
      'pharmacy': 'pharmacy',
      'medications': 'pharmacy',
      'medicine': 'pharmacy',
      'medical': 'medical',
      'ayurvedic': 'pharmacy',
      'ayurveda': 'pharmacy',
      'dental care': 'pharmacy',
      'dental-care': 'pharmacy',
      'dental': 'pharmacy',
      'wellness': 'medical',
      'health': 'medical',
      
      // Other categories
      'electronics': 'electronics',
      'clothing': 'clothing'
    };
    
    // Check for exact matches first
    if (categoryToShopType[lowerName]) {
      return categoryToShopType[lowerName];
    }
    
    // Check for partial matches (category name contains key or key contains category name)
    for (const [key, shopType] of Object.entries(categoryToShopType)) {
      if (lowerName.includes(key) || key.includes(lowerName)) {
        return shopType;
      }
    }
    
    return null;
  };

  const handleCategoryClick = (categoryId: string) => {
    // Find the category to get its API ID and name
    const allCategories = categorySections.flatMap(section => section.categories);
    const category = allCategories.find((cat: any) => cat.id === categoryId);
    
    if (!category) {
      return;
    }
    
    const categoryName = (category as any).categoryName || (category as any).name || categoryId;
    const categoryImage = (category as any).image || null;
    const categoryIcon = (category as any).icon || null;
    const categoryDisplayName = (category as any).name || categoryName;
    const shopType = getShopTypeFromCategory(categoryName);
    
    // If category maps to a shop type, navigate to explore-shops with shop type filter
    if (shopType) {
      navigate('/explore-shops', { 
        state: { 
          shopType: shopType,
          filterType: 'shopType',
          categoryName: categoryName,
          categoryDisplayName: categoryDisplayName,
          categoryImage: categoryImage,
          categoryIcon: categoryIcon
        } 
      });
    } else if (category && (category as any).categoryId) {
      // Otherwise, navigate to browse-all page with category filter (for products)
      navigate('/browse-all', { 
        state: { 
          categoryId: (category as any).categoryId,
          categoryName: categoryName,
          filterType: 'category'
        } 
      });
    } else {
      // Fallback: navigate with category name
      navigate('/browse-all', { 
        state: { 
          categoryName: categoryId,
          filterType: 'category'
        } 
      });
    }
  };

  const handleAddressSelect = (address: string) => {
    setDeliveryAddress(address);
    localStorage.setItem('delivery_address', address);
    setShowAddressModal(false);
  };

  return (
    <div className="customer-home-page">
      {/* Skip to main content for accessibility */}
      <a href="#main-content" className="skip-to-main" aria-label="Skip to main content">
        Skip to main content
      </a>
      
      {/* Header - Same as Browse All page */}
      <CustomerHeader
        showBackButton
        showLoyaltyButton
        cartCount={cartCount}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => {
          // Load cart and show modal on same page
          loadCartItems();
          setShowCartModal(true);
        }}
      />

      {/* Delivery Address Bar */}
      <nav className="delivery-address-bar" aria-label="Delivery location">
        <div className="body-container">
          <button 
            className="address-selector"
            onClick={() => setShowAddressModal(true)}
            aria-label={`Change delivery address. Current address: ${deliveryAddress}`}
            aria-expanded={showAddressModal}
          >
            <span className="address-icon">📍</span>
            <span className="address-label">{t.deliverTo || 'Deliver to'}</span>
            <span className="address-value">{deliveryAddress}</span>
            <span className="address-arrow" aria-hidden="true">▼</span>
          </button>
        </div>
      </nav>

      {/* Navigation Buttons - At Top */}
      <nav id="navigation" className="main-nav" role="navigation" aria-label="Primary">
        <div className="nav-content">
          <div className="nav-buttons">
            <button 
              className={`nav-btn ${locationPath.pathname === '/app' || locationPath.pathname === '/' || locationPath.pathname === '/customer' || locationPath.pathname === '/customer/home' ? 'active' : ''}`} 
              onClick={() => navigate('/app')}
            >
              🏠 {t.mainStore || 'Dashboard'}
            </button>
            <button 
              className={`nav-btn ${locationPath.pathname === '/explore-shops' || locationPath.pathname === '/shops' ? 'active' : ''}`} 
              onClick={() => navigate('/explore-shops')}
            >
              🔎 {t.exploreShops || 'Explore Shops'}
            </button>
            <button 
              className={`nav-btn ${locationPath.pathname === '/browse-all' || locationPath.pathname === '/browse' ? 'active' : ''}`} 
              onClick={() => navigate('/browse-all')}
            >
              🛍️ {t.browseAll || 'Browse All'}
            </button>
            <button 
              className={`nav-btn ${locationPath.pathname === '/services' || locationPath.pathname.startsWith('/services/') ? 'active' : ''}`} 
              onClick={() => navigate('/services')}
            >
              🔧 {language === 'hi' ? 'सेवाएं' : language === 'te' ? 'సేవలు' : language === 'ta' ? 'சேவைகள்' : 'Services'}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main id="main-content" role="main" aria-label="Main content">

      {/* Category Cards */}
      <section className="category-cards-section" aria-labelledby="category-cards-heading">
        <div className="body-container">
          <h2 id="category-cards-heading" className="sr-only">Featured Categories</h2>
          <div className="category-cards-grid" role="list">
            {filteredCategoryCards.map(category => (
              <article 
                key={category.id} 
                className="category-card"
                onClick={() => handleCategoryClick(category.id)}
                role="listitem"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCategoryClick(category.id);
                  }
                }}
                aria-label={`${category.name}: ${category.description}`}
              >
                <div className="category-card-top" style={{ background: category.gradient }}></div>
                <div className="category-card-body">
                  <div className="category-icon">{category.icon}</div>
                  <div className="category-info">
                    <h3>{category.name}</h3>
                    <p>{category.description}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Promotional Banner Section */}
      <section className="promotional-banner-section" aria-label="Promotional carousel">
        <div className="body-container">
          <div className="promotional-carousel-wrapper">
            <div 
              className="promotional-carousel-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {promotionalSlides.map((slide) => (
                <div key={slide.id} className="promotional-banner-slide">
                  <div className="promotional-content">
                    <h2 className="promotional-title">{slide.title}</h2>
                    <p className="promotional-description">{slide.description}</p>
                    <button 
                      className={`promotional-cta ${slide.isPrimary ? 'primary-cta' : 'secondary-cta'}`}
                      onClick={() => {
                        if (slide.ctaLink.startsWith('#')) {
                          // Scroll to section on same page
                          const element = document.querySelector(slide.ctaLink);
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        } else {
                          navigate(slide.ctaLink);
                        }
                      }}
                      aria-label={slide.ctaText}
                    >
                      {slide.ctaText} {slide.isPrimary ? '→' : ''}
                    </button>
                  </div>
                  <div className="promotional-image-container">
                    <img 
                      src={slide.image}
                      alt={slide.title}
                      className="promotional-image"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.promotional-placeholder')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'promotional-placeholder';
                          placeholder.innerHTML = '<div style="width: 100%; height: 100%; background: linear-gradient(135deg, #f59e0b 0%, #2563eb 100%); display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: 600;">Quickko</div>';
                          parent.appendChild(placeholder);
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Navigation Arrows */}
            <button 
              className="carousel-arrow carousel-arrow-left"
              onClick={goToPrev}
              aria-label="Previous slide"
            >
              ←
            </button>
            <button 
              className="carousel-arrow carousel-arrow-right"
              onClick={goToNext}
              aria-label="Next slide"
            >
              →
            </button>
          </div>
          
          {/* Carousel Controls */}
          <div className="promotional-carousel-controls">
            <button 
              className="carousel-pause" 
              onClick={toggleAutoPlay}
              aria-label={isAutoPlaying ? 'Pause carousel' : 'Play carousel'}
            >
              {isAutoPlaying ? '⏸' : '▶'}
            </button>
            <button className="carousel-prev" onClick={goToPrev} aria-label="Previous slide">←</button>
            <div className="carousel-dots">
              {promotionalSlides.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            <button className="carousel-next" onClick={goToNext} aria-label="Next slide">→</button>
          </div>
        </div>
      </section>

      {/* Category Sections (organized by groups) */}
      {categorySections.map(section => (
        <section key={section.title} className="category-section" aria-labelledby={`section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="body-container">
            <h3 id={`section-${section.title.toLowerCase().replace(/\s+/g, '-')}`} className="section-title">{section.title}</h3>
            <div className="category-grid" role="list">
            {section.categories.map((category: any) => (
              <button
                key={category.id}
                className={`category-button ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(category.id);
                  handleCategoryClick(category.id);
                }}
                aria-pressed={selectedCategory === category.id}
                aria-label={`${category.name} category`}
                role="listitem"
              >
                {category.image ? (
                  <div className="category-image-wrapper">
                    <img 
                      src={getValidImageUrl(category.image)} 
                      alt={`${category.name} category image`}
                      className="category-image"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.category-icon-fallback')) {
                          const fallback = document.createElement('span');
                          fallback.className = 'category-icon-fallback';
                          fallback.textContent = category.icon;
                          parent.appendChild(fallback);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <span className="category-icon">{category.icon}</span>
                )}
                <span className="category-name">{category.name}</span>
              </button>
            ))}
            </div>
          </div>
        </section>
      ))}

      {/* Popular Products */}
      <section className="popular-products-section" aria-labelledby="popular-products-heading">
        <div className="body-container">
          <div className="section-header">
            <h2 id="popular-products-heading" className="section-title-main">{t.popularProducts || 'Popular Products'}</h2>
                <button className="see-all-btn" onClick={() => navigate('/browse-all')}>
                  {t.seeAll || 'See All'}
                </button>
        </div>
        {isLoadingProducts ? (
          <div className="loading-state" role="status" aria-live="polite" aria-label="Loading products">
            <span className="sr-only">Loading products, please wait</span>
            {t.loadingProducts || 'Loading products...'}
          </div>
        ) : (
          <div 
            className={`products-grid products-grid-${Math.min(popularProducts.length, 10)}`}
            role="list" 
            aria-label="Popular products list"
          >
            {popularProducts.map(product => (
              <article key={product.id} className="product-card" role="listitem">
                <div className="product-image-container">
                  {product.discount && (
                    <span className="product-discount-badge" aria-label={`${product.discount} percent off`}>
                      {product.discount}% OFF
                    </span>
                  )}
                  {product.image.startsWith('http') || product.image.startsWith('/') ? (
                    <img 
                      src={product.image} 
                      alt={`${product.name} - ${product.brand}`}
                      className="product-image"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent && !parent.querySelector('.product-placeholder')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'product-placeholder';
                          placeholder.innerHTML = `<div style="width: 100%; height: 100%; background: linear-gradient(135deg, #f59e0b 0%, #2563eb 100%); display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: 600;">${product.brand}</div>`;
                          parent.appendChild(placeholder);
                        }
                      }}
                    />
                  ) : (
                    <div className="product-placeholder">
                      <span className="product-emoji">{product.image}</span>
                    </div>
                  )}
                  <button
                    className={`wishlist-btn ${isInWishlist(String(product.id)) ? 'active' : ''}`}
                    onClick={(e) => handleAddToWishlist(product, e)}
                    aria-label={isInWishlist(String(product.id)) ? 'Remove from wishlist' : 'Add to wishlist'}
                    title={isInWishlist(String(product.id)) ? 'Remove from wishlist' : 'Add to wishlist'}
                  >
                    {isInWishlist(String(product.id)) ? '❤️' : '🤍'}
                  </button>
                </div>
                <div className="product-content">
                  <span className="product-brand">{product.brand}</span>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-description">{product.weight} • {product.category}</p>
                  <div className="product-price">
                    {product.originalPrice && (
                      <span className="original-price">₹{product.originalPrice}</span>
                    )}
                    <span className="current-price">₹{product.price}</span>
                  </div>
                  <button 
                    className="product-cta" 
                    onClick={() => handleProductAdd(product)}
                    aria-label={`Add ${product.name} to cart`}
                  >
                    {t.addToCart || 'Add to Cart'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
        </div>
      </section>

      {/* Shops Near You */}
      <section className="shops-nearby-section" aria-labelledby="shops-nearby-heading">
        <div className="body-container">
          <div className="section-header">
            <h2 id="shops-nearby-heading" className="section-title-main">{t.shopsNearYou || 'Shops Near You'}</h2>
            <button className="see-all-btn" onClick={() => navigate('/explore-shops')}>
              {t.seeAll || 'See All'}
            </button>
          </div>
        {isLoadingShops ? (
          <div className="loading-state" role="status" aria-live="polite" aria-label="Loading shops">
            <span className="sr-only">Loading nearby shops, please wait</span>
            {t.loadingShops || 'Loading shops...'}
          </div>
        ) : (
          <div 
            className={`shops-list shops-list-all`}
            role="list" 
            aria-label="Nearby shops list"
          >
            {nearbyShops.map(shop => (
              <article 
                key={shop.id} 
                className="shop-card-mini"
                onClick={() => navigate(`/shop/${shop.id}`)}
                role="listitem"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/shop/${shop.id}`);
                  }
                }}
                aria-label={`${shop.name} shop`}
              >
                  <div className="shop-image-mini">
                    {shop.logo ? (
                    <img 
                      src={shop.logo} 
                      alt={`${shop.name} shop logo`}
                      loading="lazy"
                      decoding="async"
                        onError={(e) => {
                          console.error('Failed to load shop logo:', shop.logo, 'for shop:', shop.name);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent && !parent.querySelector('.shop-icon-fallback')) {
                            const fallback = document.createElement('span');
                            fallback.className = 'shop-icon-fallback';
                            fallback.textContent = '🏪';
                            parent.appendChild(fallback);
                          }
                        }}
                        onLoad={() => {
                          console.log('Successfully loaded shop logo:', shop.logo, 'for shop:', shop.name);
                        }}
                      />
                    ) : (
                      <span className="shop-icon-fallback">🏪</span>
                    )}
                  </div>
                  <div className="shop-info-mini">
                    <div className="shop-name-mini">{shop.name}</div>
                    <div className="shop-meta">
                      <span className="delivery-time">20-30 min</span>
                      <span className="shop-rating">⭐ 4.6</span>
                      <span className="shop-distance">1.2 km</span>
                    </div>
                    <div className="shop-tags">
                      {shop.type && <span className="tag">{shop.type}</span>}
                      {shop.description && (
                        <span className="tag">{shop.description.split(' ').slice(0, 2).join(' ')}</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Weekly Specials Section */}
      <section className="weekly-specials-section" aria-labelledby="weekly-specials-heading">
        <div className="body-container">
          <h2 id="weekly-specials-heading" className="section-title-main">Weekly Specials</h2>
          <div className="specials-grid" role="list" aria-label="Weekly special offers">
            {weeklySpecials.map((special) => (
              <article key={special.id} className="special-card" role="listitem">
                <div className="special-image-container">
                  {special.discount && (
                    <span className="special-discount-badge" aria-label={`${special.discount} discount`}>
                      {special.discount} OFF
                    </span>
                  )}
                  <img 
                    src={special.image}
                    alt={`${special.title} - ${special.description}`}
                    className="special-image"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.special-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'special-placeholder';
                        placeholder.innerHTML = `<div style="width: 100%; height: 100%; background: linear-gradient(135deg, #f59e0b 0%, #2563eb 100%); display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: 600; text-align: center; padding: 20px;">${special.category}</div>`;
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                </div>
                <div className="special-content">
                  <span className="special-category">{special.category}</span>
                  <h3 className="special-title">{special.title}</h3>
                  <p className="special-description">{special.description}</p>
                  <button 
                    className="special-cta"
                    onClick={() => navigate(`/specials/${special.id}`)}
                    aria-label={`View ${special.title} specials`}
                  >
                    View Specials
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Recipe Ideas Section */}
      <section className="recipe-ideas-section" aria-labelledby="recipe-ideas-heading">
        <div className="body-container">
          <h2 id="recipe-ideas-heading" className="section-title-main">Recipe Ideas</h2>
          <div className="recipes-grid" role="list" aria-label="Recipe suggestions">
            {recipeIdeas.map((recipe) => (
              <article key={recipe.id} className="recipe-card" role="listitem">
                <div className="recipe-image-container">
                  <img 
                    src={recipe.image}
                    alt={`${recipe.title} recipe`}
                    className="recipe-image"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent && !parent.querySelector('.recipe-placeholder')) {
                        const placeholder = document.createElement('div');
                        placeholder.className = 'recipe-placeholder';
                        placeholder.innerHTML = `<div style="width: 100%; height: 100%; background: linear-gradient(135deg, #10b981 0%, #059669 100%); display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: 600; text-align: center; padding: 20px;">${recipe.title}</div>`;
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                  <div className="recipe-badge">
                    <span className="recipe-time">{recipe.time}</span>
                  </div>
                </div>
                <div className="recipe-content">
                  <div className="recipe-meta">
                    <span className="recipe-servings">{recipe.servings}</span>
                    <span className="recipe-difficulty">{recipe.difficulty}</span>
                  </div>
                  <h3 className="recipe-title">{recipe.title}</h3>
                  <p className="recipe-description">{recipe.description}</p>
                  <button 
                    className="recipe-cta"
                    onClick={() => navigate(`/recipes/${recipe.id}`)}
                    aria-label={`View ${recipe.title} recipe`}
                  >
                    View Recipe
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      </main>

      {/* Bottom Navigation */}
      <div className="bottom-navigation">
        <button 
          className="nav-item active"
          onClick={() => navigate('/customer')}
          aria-label="Home"
        >
          <span className="nav-icon">🏠</span>
          <span className="nav-label">Home</span>
        </button>
        <button 
          className="nav-item"
          onClick={() => navigate('/app', { state: { view: 'discovery' } })}
          aria-label="Find Shops"
        >
          <span className="nav-icon">🏪</span>
                <span className="nav-label">{t.findShops || 'Find Shops'}</span>
        </button>
        <button 
          className="nav-item"
          onClick={() => navigate('/wishlist')}
          aria-label="Wishlist"
        >
          <span className="nav-icon">❤️</span>
                <span className="nav-label">{t.wishlist || 'Wishlist'}</span>
        </button>
        <button 
          className="nav-item"
          onClick={() => navigate('/loyalty')}
          aria-label="Loyalty Points"
        >
          <span className="nav-icon">⭐</span>
                <span className="nav-label">{t.loyalty || 'Loyalty'}</span>
        </button>
        <button 
          className="nav-item"
          onClick={() => navigate('/profile')}
          aria-label="Profile"
        >
          <span className="nav-icon">👤</span>
                <span className="nav-label">{t.profile || 'Profile'}</span>
        </button>
      </div>

      {/* Address Modal */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSelectAddress={(address) => {
          setDeliveryAddress(address);
          localStorage.setItem('delivery_address', address);
        }}
        currentAddress={deliveryAddress}
      />

      {/* Shop Selection Modal */}
      <ShopSelectionModal
        isOpen={showShopModal}
        product={selectedProductForCart as ApiProduct | null}
        onClose={() => {
          setShowShopModal(false);
          setSelectedProductForCart(null);
        }}
        onSelectShop={(shop, product) => {
          handleShopSelect(shop, product as Product);
        }}
      />

      {/* Cart Modal Overlay */}
      {showCartModal && (
        <div 
          className="cart-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCartModal(false);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cart-modal-title"
        >
          <div className="cart-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cart-modal-header">
              <h2 id="cart-modal-title">Shopping Cart</h2>
              <button 
                className="cart-modal-close"
                onClick={() => setShowCartModal(false)}
                aria-label="Close cart"
              >
                ×
              </button>
            </div>
            <div className="cart-modal-body">
              <CrossShopCart
                items={cartItems}
                savedItems={[]}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveItem={handleRemoveItem}
                onCheckout={handleCartCheckout}
                onContinueShopping={() => {
                  setShowCartModal(false);
                  navigate('/browse-all');
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerHomePage;

